import DevicesIcon from '@mui/icons-material/Devices';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { Avatar, Box, Button, Snackbar, Stack, SvgIcon, Typography } from '@mui/material';
import Alert from '@mui/material/Alert';
import InviteByEmail from 'components/@home/drawers/ProfileDrawer/Profile/EmployeeActions/InviteByEmail';
import { bool, number, object } from 'prop-types';
import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import employeeShape from 'shapes/employee';
import devicesDrawer from 'store/app/ui/drawers/devices/action';
import passkeyManagementDrawer from 'store/app/ui/drawers/passkeyManagement/action';
import { I18n } from 'utils/i18n';
import ChangeAvatar from './ChangeAvatar';
import EditProfileButton from './EditProfileButton';
import GeneralInfo from './GeneralInfo';
import InviteAgain from './InviteAgain';
import InviteLink from './InviteLink';
import InviteQRCode from './InviteQRCode';
import PrintInvitation from './PrintInvitation';
import useDownloadSignedZip from './useDownloadSignedZip';

const EmployeeActions = ({
  employee,
  readOnlyAvatar = false,
  authCompany = null,
  isCompanyAdmin = false,
  mandatoryReadsTotalCount = 0,
}) => {
  const dispatch = useDispatch();

  const openPasskeyManagement = useCallback(() => {
    dispatch(passkeyManagementDrawer.open(employee?._id));
  }, [dispatch, employee?._id]);

  const openDevicesManagement = useCallback(() => {
    dispatch(devicesDrawer.open(employee?._id));
  }, [dispatch, employee?._id]);

  const companyUsesOmmnioSign =
    authCompany?.eSignatureProvider?.isActive === true &&
    authCompany?.eSignatureProvider?.name === 'ommniosign';
  const hasLinkedUser = !!(
    (employee?.user && (employee.user._id || employee.user)) ||
    employee?.user_id
  );
  const showDownloadSignedZip =
    isCompanyAdmin && employee?._id && hasLinkedUser && companyUsesOmmnioSign;
  const hasMandatoryReads = (mandatoryReadsTotalCount ?? 0) > 0;

  const { runDownload, snackbar, setSnackbar } = useDownloadSignedZip({ employee });

  return (
    <Box sx={{ display: 'flex', marginBottom: '30px' }}>
      {readOnlyAvatar || employee.user?.isApp ? (
        <Avatar src={employee.avatar} sx={{ width: 80, height: 80 }}>
          {employee.initials}
        </Avatar>
      ) : (
        <ChangeAvatar avatar={employee.avatar} employee_id={employee._id} />
      )}
      <Box sx={{ flexGrow: 1, paddingLeft: '20px' }}>
        <GeneralInfo
          isApp={!!employee.user?.isApp}
          code={employee.code}
          name={employee.name}
          department={employee.department}
          jobTitle={employee.jobTitle}
        />
        {!employee.user?.isApp ? (
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
            }}
          >
            <EditProfileButton employeeId={employee._id} />
            {!employee.user && employee.inviteToken && <InviteLink employee={employee} />}
            {!employee.user && employee.inviteToken && <PrintInvitation employee={employee} />}
            {employee.email && <InviteByEmail employeeId={employee._id} />}
            {employee.user && <InviteAgain employeeId={employee._id} userId={employee.user._id} />}
            {employee.user && (
              <Stack
                direction="row"
                useFlexGap
                spacing={1}
                sx={{
                  flexWrap: 'wrap',
                  alignItems: 'center',
                }}
              >
                <Button
                  onClick={openPasskeyManagement}
                  variant="outlined"
                  color="secondary"
                  startIcon={
                    <SvgIcon sx={{ fontSize: 20 }}>
                      <VpnKeyIcon />
                    </SvgIcon>
                  }
                >
                  {I18n.t('ProfileDrawer.Manage passkeys')}
                </Button>
                <Button
                  onClick={openDevicesManagement}
                  variant="outlined"
                  color="secondary"
                  startIcon={
                    <SvgIcon sx={{ fontSize: 20 }}>
                      <DevicesIcon />
                    </SvgIcon>
                  }
                >
                  {I18n.t('ProfileDrawer.Manage devices')}
                </Button>
                {showDownloadSignedZip ? (
                  <Button
                    variant="outlined"
                    color="secondary"
                    disabled={!hasMandatoryReads}
                    onClick={runDownload}
                    startIcon={<FileDownloadOutlinedIcon />}
                    aria-label={I18n.t('EmployeeDrawer.ReleasePhone.downloadSignedZipAriaLabel')}
                    title={I18n.t('EmployeeDrawer.ReleasePhone.downloadSignedZipTooltip')}
                  >
                    {I18n.t('ProfileDrawer.downloadAllSignedDocs')}
                  </Button>
                ) : null}
              </Stack>
            )}
          </Box>
        ) : null}
      </Box>
      <div>
        {!employee.user && employee.inviteToken && <InviteQRCode employee={employee} />}
        {!employee.user && employee.inviteToken ? (
          <Typography style={{ textAlign: 'center', fontSize: 10 }}>
            {`${employee.firstName?.trim()} ${employee.surName?.trim()}`}
          </Typography>
        ) : null}
      </div>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
EmployeeActions.propTypes = {
  employee: employeeShape.isRequired,
  readOnlyAvatar: bool,
  authCompany: object,
  isCompanyAdmin: bool,
  mandatoryReadsTotalCount: number,
};

export default EmployeeActions;
